/**
 * visual-review: the project's Mac-local review servers (`reviewServers` in viko.project.yaml,
 * e.g. http://127.0.0.1:8765/review/), listed with what each needs and openable from any device
 * through the live-preview tunnel. Writes from an interactive page go to the host unchanged; a
 * server declared `interactive: false` is view-only. A declaration alone never opens a port: the
 * one-time share consent (per port and process) is asked in the preview frame.
 */
import { useQuery } from '@tanstack/react-query'
import type { ReviewServerStatus } from '@viko/protocol'
import { StatusIcon, type StatusKind } from '@viko/transcript-ui'
import { MonitorPlay, X } from 'lucide-react'
import { useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { useMachines, useProjects } from '../lib/queries.js'
import { activePoll } from '../lib/query-client.js'
import '../routes/project-views.css'
import { LivePreview } from './LivePreview.js'

/** State as one status icon plus its word (no chips; design-language §7). */
const STATE: Record<ReviewServerStatus['state'], { text: string; cls: string; kind: StatusKind }> =
  {
    ready: { text: 'Ready', cls: 'pv-word-ok', kind: 'ok' },
    needs_share: { text: 'Needs your OK', cls: 'pv-word-wait', kind: 'wait' },
    down: { text: 'Not running', cls: '', kind: 'idle' },
    invalid: { text: 'Cannot be opened', cls: 'pv-word-blocked', kind: 'blocked' },
  }

function why(s: ReviewServerStatus): string {
  switch (s.state) {
    case 'ready':
      return 'Open it from here, on any device.'
    case 'needs_share':
      return `Listening on port ${s.port}${s.process ? ` (${s.process})` : ''}. The first open asks you to allow sharing that port, once.`
    case 'down':
      return `Nothing is listening on port ${s.port} right now. Start the review server on the machine.`
    case 'invalid':
      return s.problem ?? 'This address cannot be tunnelled.'
  }
}

export function ReviewServers({ projectId }: { projectId: string }) {
  const b = useBackend()
  const projects = useProjects()
  const machines = useMachines()
  const project = projects.data?.find((p) => p.id === projectId)
  const wanted = new Set([
    ...(project?.checkouts?.map((c) => c.machineId) ?? []),
    ...(project?.machines ?? []),
  ])
  const candidates = (machines.data ?? []).filter((m) => wanted.has(m.id))
  const machine = candidates.find((m) => m.online) ?? candidates[0]
  const q = useQuery({
    queryKey: ['review-servers', projectId, machine?.id],
    queryFn: async () => {
      const r = await b.dispatch('preview.review_servers', machine?.id as string, { projectId })
      return (r.result as { servers?: ReviewServerStatus[] } | undefined)?.servers ?? []
    },
    enabled: !!machine?.online,
    ...activePoll(20_000),
    retry: false,
  })
  const [open, setOpen] = useState<string | undefined>()
  const servers = q.data ?? []
  if (!machine || !servers.length) return null
  const where = machine.platform === 'win32' ? 'the PC' : machine.name
  return (
    <section aria-label="Review servers" className="pv-sec">
      <h3 className="pv-h">
        Review servers <span className="pv-h__count">{servers.length}</span>
        <span className="pv-h__aside">on {where}</span>
      </h3>
      <ul className="pv-rows">
        {servers.map((s) => {
          const st = STATE[s.state]
          const opened = open === s.name
          return (
            <li key={s.name} data-review-server={s.name} data-state={s.state}>
              <div className="pv-row pv-row--stack">
                <span className="pv-row__icon">
                  <StatusIcon kind={st.kind} label={false} />
                </span>
                <span className="pv-row__main">
                  <span className="pv-row__title">
                    <strong>{s.name}</strong>
                    <span className="pv-sub">
                      {' · '}
                      <span className={st.cls}>{st.text}</span>
                      {' · '}
                      {s.interactive ? `Interactive: saves go to ${where}` : 'View only'}
                    </span>
                  </span>
                  <span className="pv-row__meta">{why(s)}</span>
                  <span className="pv-row__meta">
                    <code className="pv-mono">{s.url}</code>
                  </span>
                </span>
                <span className="pv-row__aside">
                  {opened ? (
                    <button type="button" className="pv-btn" onClick={() => setOpen(undefined)}>
                      <X size={16} aria-hidden="true" />
                      Close
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="pv-btn"
                      disabled={s.state === 'down' || s.state === 'invalid'}
                      onClick={() => setOpen(s.name)}
                      aria-label={`Open ${s.name}`}
                    >
                      <MonitorPlay size={16} aria-hidden="true" />
                      Open
                    </button>
                  )}
                </span>
                {opened ? (
                  <div className="pv-row__detail">
                    <LivePreview
                      backend={b}
                      machineId={machine.id}
                      projectId={projectId}
                      url={s.url}
                      title={s.name}
                      platform={machine.platform}
                      online={machine.online}
                      snapshot={() => (
                        <p role="status" className="pv-empty">
                          {s.name} runs on {where}, which is not reachable right now.
                        </p>
                      )}
                    />
                  </div>
                ) : null}
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
